import React, { useEffect, useState } from 'react';
import styles from './index.module.scss';
import { DATE_FORMATS, PO_STATUS, formatDate, showError, showSuccess } from 'utils';
import FileUploadComponent from 'components/FileUploadComponent/index';
import { fetchPurchaseOrderDetail, updatePurchaseOrder, uploadInvoiceForPurchaseOrder } from 'services';
import ActionButton from 'components/ActionButton';
import CopyToClipBoard from 'components/CopyToClipBoard';
import OrderItems from 'pages/purchase-order/detail/OrderItems';
import moment from 'moment';
import { Loading } from 'components/loading/Loading';
import DatePicker from 'components/Date/DatePicker';

const UploadPublicInvoice = (props) => {
  const [invoice, setInvoice] = React.useState(null);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [poDetails, setPODetails] = useState<any>();
  const [purchaseOrder, setPurchaseOrder] = useState<any>();
  const [deliveryDate, setDeliveryDate] = useState<string | null>();
  const [comment, setComment] = useState<string>();
  const [editable, setEditable] = useState<boolean>(false);
  const [isPOAcceptedLoading, setIsPOAcceptedLoading] = useState<boolean>(false);
  const { poId } = props.match.params;

  useEffect(() => {
    fetchPODetails();
  }, []);

  const updatePODetails = (items: Record<string, any>) => {
    setPODetails({ ...poDetails, purchaseOrderItems: items });
  };

  const updatePurchaseOrderFn = async (status: boolean) => {
    setIsPOAcceptedLoading(true);
    try {
      if (status) {
        await updatePurchaseOrder({ id: poId, status: PO_STATUS.ACCEPTED });
      } else {
        await updatePurchaseOrder({ id: poId, status: PO_STATUS.REJECTED });
      }
      await fetchPODetails();
    } catch (e) {
      showError(e);
    } finally {
      setIsPOAcceptedLoading(false);
    }
  };
  const uploadInvoice = async () => {
    setLoading(true);
    if (!invoice) {
      showError('Invoice File is required');
      setLoading(false);
      return;
    }
    const data = {
      id: poId,
      invoice,
    };

    try {
      await uploadInvoiceForPurchaseOrder(data);
      await fetchPODetails();
      showSuccess('Invoice uploaded successfully.');
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchPODetails = async () => {
    setLoading(true);
    try {
      const response = await fetchPurchaseOrderDetail(poId);
      setPODetails(response);
      setPurchaseOrder(response?.purchaseOrder);
      setDeliveryDate(response?.purchaseOrder?.deliveryDate);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  const handlePOUpdate = async () => {
    setLoading(true);
    try {
      await updatePurchaseOrder({ id: poId, deliveryDate, comment });
      await fetchPODetails();
      setEditable(false);
      showSuccess('Purchase Order updated successfully.');
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  const getUploadSection = () => (
    <div className={styles['upload-body']}>
      <div className={styles['upload-container']}>
        <div className={styles['label']}>Selected File</div>
        <FileUploadComponent
          entityId={''}
          showWebcamImagePicker={true}
          uploadAPI={(id, file) => setInvoice(file)}
        />
      </div>
      <ActionButton
        isSubmit={false}
        active={!!invoice}
        isLoading={loading}
        text={'Submit'}
        onClick={uploadInvoice}
        customClass={styles['submit-button']}
      />
    </div>
  );

  const getInvoiceInfo = () => {
    return (<div className={styles['invoice-info']}>
      <div className={styles['invoice-preview']}>
        <div className={styles['label']}>Invoice Preview</div>
        <img src={purchaseOrder?.invoice?.url} />
      </div>
      <div className={styles['invoice-url']}>
        <div className={styles['label']}>Invoice URL</div>
        <div className={'d-flex align-items-center'}>
          <a href={purchaseOrder?.invoice?.url} target={'blank'}>{purchaseOrder?.invoice?.url}</a>
          <CopyToClipBoard copyText={purchaseOrder?.invoice.url} />
        </div>
      </div>
    </div>
    );
  };

  const getDeliveryDate = () => {
    if (editable) {
      return (
        <div className={styles['input-group']}>
          <div className={styles['label']}>Delivery Date</div>
          <DatePicker
            date={moment(deliveryDate).format(DATE_FORMATS.YYYY_MM_DD)}
            onChange={(e: any) => setDeliveryDate(formatDate(e, DATE_FORMATS.YYYY_MM_DD))}
          />
        </div>
      );
    } else {
      return (
        <div className={styles['input-group']}>
          <div className={styles['label']}>Delivery Date</div>
          {purchaseOrder?.deliveryDate ?? '-'}
        </div>
      );
    }
  };

  const getPOAcceptReject = () => {
    if (purchaseOrder?.status === PO_STATUS.SENT) {
      return (
        <>
          <div className='d-flex align-self-end mt-2'>
            <ActionButton
              active
              isLoading={isPOAcceptedLoading}
              onClick={() => updatePurchaseOrderFn(true)}
              text={'Accept'}
              customClass={styles['accept']}
            />
            <ActionButton
              active
              isLoading={isPOAcceptedLoading}
              onClick={() => updatePurchaseOrderFn(false)}
              text={'Reject'}
              customClass={styles['reject']}
            />
          </div>
        </>
      );
    } else return null;
  };

  const getCommentInput = () => {
    if (editable) {
      return (
        <div className={`${styles['input-group']} comment-section`}>
          <div className={styles['label']}>Comment</div>
          <textarea
            value={comment}
            defaultValue={purchaseOrder?.comment}
            onChange={e => setComment(e.target.value)}
            className={`${styles['comment-box']} form-control`}
          />
        </div>
      );
    } else {
      return (
        <div className={`${styles['input-group']} comment-section`}>
          <div className={styles['label']}>Comment</div>
          {purchaseOrder?.comment ?? '-'}
        </div>
      );
    }
  };

  const getSubmitButton = () => {
    return (
      <div className='ml-auto d-flex'>
        {editable && <ActionButton
          active
          isLoading={loading}
          onClick={handlePOUpdate}
          text={'Save'}
          customClass={`${styles['submit-button']} mr-2`}
        />}
        <ActionButton
          text={editable ? 'Cancel' : 'Edit'}
          active={true}
          isLoading={false}
          customClass={`${styles['submit-button']} mr-4 mb-2`}
          onClick={() => setEditable(!editable)}
        />
      </div>
    );
  };

  const getPODetailsSection = () => {
    return (
      <div className={`${styles['details-section']}`}>
        {getDeliveryDate()}
        {getCommentInput()}
        {getSubmitButton()}
      </div>
    );
  };

  const getInvoiceContainer = () => (
    <>
      <div className={styles['subheading']}>Upload Invoice</div>
      <div className={`${styles['main-upload-container']} d-flex flex-wrap`}>
        {purchaseOrder?.invoice?.url ? getInvoiceInfo() : getUploadSection()}
      </div>
    </>
  );

  const getOrderItems = () => (
    <>
      <div className={styles['subheading']}>Order Items</div>
      <OrderItems
        {...props}
        items={poDetails?.purchaseOrderItems}
        setValue={updatePODetails}
        poStatus={poDetails?.purchaseOrder?.status}
        fetchPODetails={fetchPODetails}
        isPublic={true}
      />
    </>
  );

  const getHeading = () => (
    <div className={styles['header']}>
      <div className={styles['heading']}>
        {'Purchase Order'}
      </div>
    </div>
  );

  if (loading) return <Loading />;

  return (
    <div className='main-container'>
      {getHeading()}
      {getOrderItems()}
      {getPOAcceptReject()}
      {getPODetailsSection()}
      {getInvoiceContainer()}
    </div>
  );
};
export default UploadPublicInvoice;
